import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { setAccessToken } from './auth/tokenStore'
import './App.css'
import Header from './components/Header'
import EnhancedImageUpload from './components/EnhancedImageUpload'
import AnalysisResults from './components/AnalysisResults'
import Footer from './components/Footer'
import DebugPanel from './components/DebugPanel'
import FeedbackSystem from './components/FeedbackSystem'
import LearningTracker from './components/LearningTracker'

// Shows the user's name + sign-out when authenticated, or a sign-in button otherwise.
function AuthButton() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <span style={{ color: '#888', fontSize: '0.9rem' }}>…</span>;
  }

  if (auth.isAuthenticated) {
    const name = auth.user?.profile.given_name || auth.user?.profile.name || auth.user?.profile.email;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ color: '#666', fontSize: '0.9rem' }}>Hi, {name}</span>
        <button
          onClick={() => auth.signoutRedirect()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#ffffff',
            color: '#8B5DFF',
            border: '2px solid #8B5DFF',
            borderRadius: '25px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'all 0.3s ease'
          }}
        >
          Sign out
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={() => auth.signinRedirect()}
      style={{
        padding: '10px 20px',
        backgroundColor: '#8B5DFF',
        color: 'white',
        border: '2px solid #8B5DFF',
        borderRadius: '25px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease'
      }}
    >
      Sign in
    </button>
  );
}

// Rendered at /callback while the OIDC redirect is being processed.
function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Once react-oidc-context has finished the code->token exchange, hand control back
  // to react-router so the SPA leaves /callback and the query string is dropped.
  useEffect(() => {
    if (!auth.isLoading && (auth.isAuthenticated || auth.error)) {
      navigate('/', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #8B5DFF',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }}
        />
        <p style={{ color: '#666' }}>{auth.error ? `Sign-in error: ${auth.error.message}` : 'Signing you in…'}</p>
      </div>
    </div>
  );
}

export interface AnalysisResult {
  prediction: 'boy' | 'girl' | null
  confidence: number
  analysis: string
  predictionId?: number
  // Debug visualization
  normalizedImageBase64?: string
  nubRegionBase64?: string
  detectedSpineAngle?: number
  detectedNubAngle?: number
}

interface ApiCall {
  timestamp: string
  method: string
  url: string
  status: number
  request?: any
  response?: any
  error?: string
}

interface LearningStats {
  totalPredictions: number
  correctPredictions: number
  feedbackReceived: number
  modelVersion: string
  lastRetrained?: string
}

function MainApp() {
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult>({
    prediction: null,
    confidence: 0,
    analysis: ''
  })
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showDebug, setShowDebug] = useState(false)
  const [showLearning, setShowLearning] = useState(false)
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([])
  const [learningStats, setLearningStats] = useState<LearningStats>({
    totalPredictions: 0,
    correctPredictions: 0,
    feedbackReceived: 0,
    modelVersion: '1.0.0'
  })

  // Determine current theme based on analysis result
  const currentTheme = analysisResult.prediction === 'boy' ? 'boy' : 
                      analysisResult.prediction === 'girl' ? 'girl' : 'neutral'

  // Get theme colors
  const getThemeColors = () => {
    switch (currentTheme) {
      case 'boy':
        return { primary: '#4A90E2', light: '#e3f2fd' }
      case 'girl':
        return { primary: '#FF6B9D', light: '#fce4ec' }
      default:
        return { primary: '#8B5DFF', light: '#f8f4ff' }
    }
  }

  const themeColors = getThemeColors()

  const addApiCall = (call: ApiCall) => {
    setApiCalls(prev => [...prev, call])
  }

  const handleImageAnalysis = async (file: File, knownGender?: string, isTrainingMode?: boolean) => {
    setIsAnalyzing(true)
    const timestamp = new Date().toLocaleTimeString()
    let apiCall: ApiCall = {
      timestamp,
      method: 'POST',
      url: '/api/prediction/predict',
      status: 0
    }
    
    try {
      // Convert file to base64
      const base64 = await fileToBase64(file)
      const requestBody = {
        imageData: base64,
        fileName: file.name,
        knownGender: knownGender,
        isTrainingMode: isTrainingMode
      }
      
      apiCall.request = { fileName: file.name, imageSizeKB: Math.round(base64.length / 1024) }
      
      // Call the backend API
      const response = await fetch('/api/prediction/predict', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })

      apiCall.status = response.status

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      apiCall.response = result
      addApiCall(apiCall)
      
      const predictionId = result.predictionId || Date.now()
      
      setAnalysisResult({
        prediction: result.prediction.toLowerCase() as 'boy' | 'girl',
        confidence: Math.round(result.confidence * 100),
        analysis: result.analysis,
        predictionId,
        // Debug visualization
        normalizedImageBase64: result.normalizedImageBase64,
        nubRegionBase64: result.nubRegionBase64,
        detectedSpineAngle: result.detectedSpineAngle,
        detectedNubAngle: result.detectedNubAngle
      })

      // Update learning stats
      setLearningStats(prev => ({
        ...prev,
        totalPredictions: prev.totalPredictions + 1,
        modelVersion: result.modelVersion || prev.modelVersion
      }))
      
      // Show feedback immediately if training mode
      if (isTrainingMode && result.wasCorrect !== undefined) {
        const message = result.wasCorrect 
          ? '✅ Correct prediction! The AI successfully identified the gender.'
          : '❌ Incorrect prediction. The AI will learn from this example.'
        
        // Update stats for training mode
        setLearningStats(prev => ({
          ...prev,
          feedbackReceived: prev.feedbackReceived + 1,
          correctPredictions: prev.correctPredictions + (result.wasCorrect ? 1 : 0)
        }))
        
        setTimeout(() => alert(message), 500)
      }

    } catch (error) {
      console.error('Error calling API:', error)
      apiCall.error = error instanceof Error ? error.message : 'Unknown error'
      apiCall.status = apiCall.status || 0
      addApiCall(apiCall)
      
      // Fallback to mock analysis if API call fails
      console.log('Falling back to mock analysis')
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const predictions = ['boy', 'girl'] as const
      const randomPrediction = predictions[Math.floor(Math.random() * predictions.length)]
      const confidence = Math.floor(Math.random() * 30) + 70
      
      setAnalysisResult({
        prediction: randomPrediction,
        confidence,
        analysis: `Based on nub theory analysis, the genital tubercle angle and characteristics suggest a ${randomPrediction}. The scan quality and gestational age appear optimal for this prediction method. (Note: API connection failed, using fallback prediction)`,
        predictionId: Date.now()
      })

      // Update learning stats for fallback
      setLearningStats(prev => ({
        ...prev,
        totalPredictions: prev.totalPredictions + 1
      }))
    }
    
    setIsAnalyzing(false)
  }

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = error => reject(error)
    })
  }

  const handleFeedback = async (actualGender: string, isNotBabyScan?: boolean) => {
    if (!analysisResult.predictionId) return

    const timestamp = new Date().toLocaleTimeString()
    let apiCall: ApiCall = {
      timestamp,
      method: 'POST',
      url: 'https://localhost:7195/api/prediction/feedback',
      status: 0
    }

    try {
      const requestBody = {
        predictionId: analysisResult.predictionId,
        actualGender: actualGender || '',
        isNotBabyScan: isNotBabyScan || false
      }
      
      apiCall.request = requestBody

      const response = await fetch('/api/prediction/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })

      apiCall.status = response.status

      if (response.ok) {
        const result = await response.json()
        apiCall.response = result
        
        // Update learning stats
        const wasCorrect = analysisResult.prediction?.toLowerCase() === actualGender.toLowerCase()
        setLearningStats(prev => ({
          ...prev,
          feedbackReceived: prev.feedbackReceived + 1,
          correctPredictions: prev.correctPredictions + (wasCorrect ? 1 : 0),
          lastRetrained: wasCorrect && (prev.feedbackReceived + 1) % 10 === 0 ? 
            new Date().toLocaleTimeString() : prev.lastRetrained
        }))
      } else {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      addApiCall(apiCall)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      apiCall.error = error instanceof Error ? error.message : 'Unknown error'
      apiCall.status = apiCall.status || 0
      addApiCall(apiCall)
      
      // Still update local stats even if API fails
      const wasCorrect = analysisResult.prediction?.toLowerCase() === actualGender.toLowerCase()
      setLearningStats(prev => ({
        ...prev,
        feedbackReceived: prev.feedbackReceived + 1,
        correctPredictions: prev.correctPredictions + (wasCorrect ? 1 : 0)
      }))
    }
  }

  const resetAnalysis = () => {
    setAnalysisResult({
      prediction: null,
      confidence: 0,
      analysis: ''
    })
  }

  return (
    <div className={`app app--${currentTheme}`}>
      <Header theme={currentTheme} />
      <main className="main-content">
        <div className="container">
          {/* Navigation buttons */}
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <button
              onClick={() => setShowLearning(false)}
              style={{
                padding: '10px 20px',
                marginRight: '10px',
                backgroundColor: !showLearning ? themeColors.primary : '#ffffff',
                color: !showLearning ? 'white' : themeColors.primary,
                border: `2px solid ${themeColors.primary}`,
                borderRadius: '25px',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
            >
              Gender Prediction
            </button>
            <button
              onClick={() => setShowLearning(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: showLearning ? themeColors.primary : '#ffffff',
                color: showLearning ? 'white' : themeColors.primary,
                border: `2px solid ${themeColors.primary}`,
                borderRadius: '25px',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
            >
              🧠 AI Learning
            </button>
            <span style={{ marginLeft: '10px', display: 'inline-flex', verticalAlign: 'middle' }}>
              <AuthButton />
            </span>
          </div>

          {showLearning ? (
            <LearningTracker stats={learningStats} isVisible={true} />
          ) : (
            <>
              {!analysisResult.prediction ? (
                <EnhancedImageUpload 
                  onImageUpload={handleImageAnalysis} 
                  isAnalyzing={isAnalyzing}
                />
              ) : (
                <>
                  <AnalysisResults 
                    result={analysisResult} 
                    onReset={resetAnalysis}
                  />
                  <FeedbackSystem
                    predictionId={analysisResult.predictionId}
                    predictedGender={analysisResult.prediction || ''}
                    confidence={analysisResult.confidence}
                    onFeedbackSubmit={handleFeedback}
                    isVisible={true}
                  />
                </>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      
      {/* Debug Panel */}
      <DebugPanel
        isVisible={showDebug}
        onToggle={() => setShowDebug(!showDebug)}
        apiCalls={apiCalls}
        onClear={() => setApiCalls([])}
      />
    </div>
  )
}

function App() {
  const [triedSilent, setTriedSilent] = useState(false);
  const auth = useAuth();

  // Keep the axios token store in sync with the current session so API calls
  // carry the bearer token. Runs whenever the auth user changes.
  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null);
  }, [auth.user]);

  // Zero-click SSO: on first load, silently ask the ID server (prompt=none in a hidden
  // iframe) whether there's an existing session. If yes, we sign in with no interaction;
  // if not, the call fails quietly and the user stays logged out (login is optional).
  // Only same-site origins (e.g. *.local.letech.solutions) can pass the cookie here.
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || triedSilent) return;
    const path = window.location.pathname;
    if (path.startsWith('/callback') || path.startsWith('/silent-renew')) return;
    setTriedSilent(true);
    auth.signinSilent().catch(() => { /* no existing session — stay anonymous */ });
  }, [auth, auth.isLoading, auth.isAuthenticated, triedSilent]);

  return (
    <Router>
      <Routes>
        {/* OIDC sign-in landing. react-oidc-context processes the code/state on load
            and the AuthCallback sends the user back to "/", so this is just a spinner. */}
        <Route path="/callback" element={<AuthCallback />} />
        {/* Hidden iframe target for silent token renewal. */}
        <Route path="/silent-renew" element={<div />} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </Router>
  )
}

export default App
